import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Video, AudioLines, Image, FileText, Files, Layout } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAppSelector } from '@/store/hooks';
import { selectItemsForParent } from '@/store/selectors';

interface ProjectFooterProps {
  baseUrl: string;
  active?: string;
}

const FILE_TABS = [
  { key: 'video', label: 'Videos', icon: Video },
  { key: 'audio', label: 'Audios', icon: AudioLines },
  { key: 'image', label: 'Images', icon: Image },
  { key: 'doc', label: 'Documents', icon: FileText },
  { key: 'allfiles', label: 'All files', icon: Files },
] as const;

export function ProjectFooter({ baseUrl, active }: ProjectFooterProps) {
  const { id: projectId } = useParams<{ id: string }>();
  const items = useAppSelector(selectItemsForParent(projectId)) as Array<{ type: string }> | null;

  const counts = useMemo(() => {
    if (!items) return {} as Record<string, number>;
    const result: Record<string, number> = { all: items.length };
    for (const item of items) {
      result[item.type] = (result[item.type] || 0) + 1;
    }
    return result;
  }, [items]);

  const isMainView = !active;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/60 bg-card/80 px-4 py-2 backdrop-blur-xl">
      <ul className="mx-auto flex w-fit list-none gap-1 p-0 m-0 overflow-x-auto">
        {/* Main view link */}
        <li>
          <Link
            to={baseUrl}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm no-underline whitespace-nowrap transition-colors',
              isMainView
                ? 'bg-secondary font-semibold text-foreground'
                : 'text-muted-foreground hover:bg-secondary hover:text-accent-foreground',
            )}
          >
            <Layout className="h-4 w-4" />
            <span>Main</span>
          </Link>
        </li>

        <li className="mx-1 border-l border-border" />

        {/* File type tabs */}
        {FILE_TABS.map(({ key, label, icon: Icon }) => {
          const isActive = active === key;
          const count = key === 'allfiles' ? (counts.all ?? 0) : (counts[key] ?? 0);
          return (
            <li key={key}>
              <Link
                to={`${baseUrl}/${key}`}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm no-underline whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-secondary font-semibold text-foreground'
                    : 'text-muted-foreground hover:bg-secondary hover:text-accent-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
                <span className="text-xs text-muted-foreground">({count})</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
